/**
 * ChatInput (Sprint 7 B5; rich editor 2026-10-04).
 *
 * Soạn câu hỏi bằng RichMarkdownEditor chế độ gọn (định dạng, bảng, công thức MathType, code —
 * như diễn đàn / làm bài) — gửi đi vẫn là markdown. Ctrl/⌘+Enter hoặc nút "Gửi" để gửi;
 * Enter xuống dòng. Vẫn soạn được khi đang nhận trả lời — chỉ chặn gửi.
 */

import { useRef, useState } from "react";
import { Button, Typography } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { LazyRichMarkdownEditor } from "@/components/RichMarkdownEditor/LazyRichMarkdownEditor";

const { Text } = Typography;

// Khớp giới hạn backend (POST /conversations/<id>/messages/ — content > 4000 ký tự → 400).
const MAX_CHARS = 4000;

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Trạng thái hiện cạnh nút Gửi (vd "Đang nhận trả lời..."). */
  status?: string;
}

export function ChatInput({ onSend, disabled = false, placeholder, status }: Props) {
  const [text, setText] = useState("");
  // Bản mới nhất ngay cả khi state chưa render lại (Ctrl+Enter gửi ngay sau onChange).
  const textRef = useRef("");

  const update = (markdown: string) => {
    textRef.current = markdown;
    setText(markdown);
  };

  const handleSend = (markdown = textRef.current) => {
    const trimmed = markdown.trim();
    if (!trimmed || disabled || trimmed.length > MAX_CHARS) return;
    onSend(trimmed);
    update("");
  };

  const length = text.trim().length;
  const over = length > MAX_CHARS;

  return (
    <div
      data-testid="chat-input"
      style={{
        padding: 12,
        borderTop: "1px solid #f0f0f0",
        background: "#fff",
      }}
    >
      <LazyRichMarkdownEditor
        value={text}
        onChange={update}
        onSubmit={handleSend}
        height={180}
        placeholder={placeholder ?? "Hỏi AI Tutor… gõ “/” để chèn bảng, code, công thức"}
        warmUpMath={false}
        compact
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: 8,
        }}
      >
        <Text type={over ? "danger" : "secondary"} style={{ fontSize: 12 }}>
          {status ??
            (over
              ? `Câu hỏi quá dài (${length.toLocaleString("vi-VN")}/${MAX_CHARS.toLocaleString("vi-VN")} ký tự)`
              : "Ctrl+Enter để gửi")}
        </Text>
        <Button
          data-testid="chat-send-btn"
          type="primary"
          icon={<SendOutlined />}
          onClick={() => handleSend()}
          disabled={disabled || !length || over}
        >
          Gửi
        </Button>
      </div>
    </div>
  );
}
